/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useReducer, useCallback, useContext, createContext, useMemo } from "react";
import { v4 as uuid } from "uuid";

import Papa from "papaparse";
import { saveAs } from "file-saver";
import {
	fetchContactsAction,
	filterContactsAction,
	deleteContactsAction,
	showDeleteConfirmationAction,
	cancelDeleteConfirmationAction,
	toggleSelectAllContactsAction,
	toggleSelectContactAction,
	startEditContactAction,
	startNewContactAction,
	cancelEditContactAction,
	saveContactAction,
	createContactsAction,
	fetchAzureAdUsersAction,
	fetchRemoteConfigAction,
} from "./actions";
import { reducer } from "./reducer";
import { isEmptyObject } from "../../../../utility/helpers";

const initialState = {
	contacts: [],
	contactsLoading: false,
	contactToEdit: {},
	currentSearchTerm: "",
	deleteModal: {
		title: ``,
		message: ``,
		onClose: () => {},
		confirmOpen: false,
	},
	azureAdUsers: [],
	azureAdGroups: [],
	azureAdUsersLoading: false,
	azureAdLoadError: false,
	isAzureConfigLoading: false,
	azureConfig: {},
};

const exportContactsToCsvFile = (contacts) => {
	const csv = Papa.unparse(contacts, {
		delimiter: ",",
		header: true,
		columns: ["fullName", "phone", "tags", "azureAdUserId"],
	});

	const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
	saveAs(blob, "external-contacts.csv");
};

const parseContactsFromCsv = (csvData) => {
	const parseResults = Papa.parse(csvData, {
		header: true,
		skipEmptyLines: "greedy",
	});

	const parseErrors = [];

	const headersPresent = ["fullName", "phone", "tags", "azureAdUserId"].every((prop) =>
		parseResults.meta.fields.includes(prop),
	);

	if (!parseResults.meta?.fields || !headersPresent) {
		parseErrors.push("CSV header is missing or invalid, expected 'fullName,phone,tags,azureAdUserId'");
	}

	if (parseResults.errors.length > 0) {
		parseResults.errors.forEach((e) => {
			if (e.row) {
				parseErrors.push(`Row ${e.row + 1} - ${e.message}`);
			} else {
				parseErrors.push(e.message);
			}
		});
	}

	const results = parseResults.data.map((item) => {
		const c = {
			contactId: uuid(),
			fullName: item.fullName ? item.fullName.trim() : "",
			phone: item.phone ? item.phone.trim() : "",
			tags: !item.tags
				? []
				: item.tags
						.split(",")
						.map((tag) => tag.trim())
						.filter((tag) => !!tag),
			azureAdUserId: item.azureAdUserId ? item.azureAdUserId.trim() : "",
		};

		return {
			...c,
			...validateContact(c),
		};
	});

	return {
		parseErrors,
		results,
	};
};

const validateContact = (contact) => {
	const errors = {};

	if (!contact.fullName || contact.fullName?.length < 2 || contact.fullName?.length > 50) {
		errors["fullName"] = "name is required and must be between 2 and 50 characters.";
	}

	const e164Pattern = new RegExp(/^\+[1-9]\d{1,14}$/);

	if (contact.phone && !e164Pattern.test(contact.phone)) {
		errors["phone"] = "phone must be in E.164 format.";
	}

	if (contact.tags?.length > 3) {
		errors["tags"] = "contacts cannot have more than 3 tags.";
	}

	if (contact.tags?.some((t) => t.length > 15)) {
		errors["tags"] = "tag cannot be longer than 15 characters.";
	}

	return {
		isValid: Object.keys(errors).length === 0,
		errors,
	};
};

const Context = createContext();

export const useExternalContacts = () => useContext(Context);

export const ExternalContactsProvider = ({ children, showSnackbar }) => {
	const [state, dispatch] = useReducer(reducer, initialState);

	const isEditingContact = useMemo(() => !isEmptyObject(state.contactToEdit), [state.contactToEdit]);
	const filteredContacts = useMemo(() => state.contacts.filter(({ isFiltered }) => isFiltered), [state.contacts]);
	const filteredContactsCount = useMemo(() => filteredContacts.length, [filteredContacts]);
	const contactsCount = useMemo(() => state.contacts?.length || 0, [state.contacts]);
	const selectedContacts = useMemo(
		() => state.contacts.filter(({ isSelected, isFiltered }) => isFiltered && isSelected),
		[state.contacts],
	);
	const selectedContactIds = useMemo(() => selectedContacts.map(({ contactId }) => contactId), [selectedContacts]);
	const selectedContactsCount = useMemo(() => selectedContacts.length, [selectedContacts]);
	const areAllContactsSelected = useMemo(() => {
		if (filteredContacts.length < 1) {
			return false;
		}
		return selectedContacts.length === filteredContacts.length;
	}, [selectedContacts, filteredContacts]);

	const fetchContacts = useCallback(async () => {
		await fetchContactsAction(dispatch, { showSnackbar });
	}, [showSnackbar]);

	const filterContacts = useCallback((searchTerm) => filterContactsAction(dispatch, { searchTerm }), []);

	const exportContactsToCsv = useCallback(() => {
		const contactsToExport = selectedContacts.length > 0 ? selectedContacts : state.contacts;
		exportContactsToCsvFile(contactsToExport);
		showSnackbar("Successfully exported contacts.", "success");
	}, [selectedContacts, state?.contacts, showSnackbar]);

	const parseContactsCsv = useCallback((csvData) => {
		return parseContactsFromCsv(csvData);
	}, []);

	const deleteContacts = useCallback(
		async (contactIdsToDelete) => {
			await deleteContactsAction(dispatch, { showSnackbar, contactIdsToDelete });
		},
		[showSnackbar],
	);

	const showDeleteConfirmation = useCallback((deleteCount, onConfirm, onCancel) => {
		showDeleteConfirmationAction(dispatch, { deleteCount, onConfirm, onCancel });
	}, []);

	const cancelDeleteConfirmation = useCallback(() => {
		cancelDeleteConfirmationAction(dispatch);
	}, []);

	const toggleSelectAllContacts = useCallback(() => {
		toggleSelectAllContactsAction(dispatch, { filteredContacts });
	}, [filteredContacts]);

	const toggleSelectContact = useCallback((contactId) => {
		toggleSelectContactAction(dispatch, { contactId });
	}, []);

	const startEditContact = useCallback((contactId) => {
		startEditContactAction(dispatch, { contactId });
	}, []);

	const startNewContact = useCallback(() => {
		startNewContactAction(dispatch);
	}, []);

	const cancelEditContact = useCallback(() => {
		cancelEditContactAction(dispatch);
	}, []);

	const saveContact = useCallback(
		async (contact) => {
			await saveContactAction(dispatch, { contact, showSnackbar });
		},
		[showSnackbar],
	);

	const createContacts = useCallback(
		async (contacts) => {
			await createContactsAction(dispatch, { contacts, showSnackbar });
		},
		[showSnackbar],
	);

	/**
	 * fetches Azure Users and Groups
	 * @param {Boolean} isInBackground `true` to loading flag will not set.
	 * @returns Azure Groups and Users will be loaded.
	 */
	const fetchAzureAdUsers = useCallback(
		async (isInBackground = false) => await fetchAzureAdUsersAction(dispatch, { isInBackground }),
		[],
	);

	const fetchRemoteConfig = useCallback(async () => await fetchRemoteConfigAction(dispatch), []);

	return (
		<Context.Provider
			value={{
				...state,
				selectedContacts,
				selectedContactsCount,
				filteredContacts,
				filteredContactsCount,
				contactsCount,
				selectedContactIds,
				areAllContactsSelected,
				isEditingContact,
				showSnackbar,
				fetchContacts,
				filterContacts,
				deleteContacts,
				showDeleteConfirmation,
				cancelDeleteConfirmation,
				exportContactsToCsv,
				toggleSelectAllContacts,
				toggleSelectContact,
				startEditContact,
				startNewContact,
				cancelEditContact,
				validateContact,
				saveContact,
				createContacts,
				parseContactsCsv,
				fetchRemoteConfig,
				fetchAzureAdUsers,
			}}
		>
			{children}
		</Context.Provider>
	);
};
